import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  getTrialBalance,
  getIncomeStatement,
  getBalanceSheet,
  getAccountSummary,
  getJournalSummary,
  getInvoiceSummary,
  getAccountsReceivablePayable,
  getMonthlyIncomeExpense,
  getPendingTasks
} from '../services/api';

interface ReportData {
  accountSummary: any;
  journalSummary: any;
  invoiceSummary: any;
  trialBalance: any;
  incomeStatement: any;
  balanceSheet: any;
  accountsReceivable: any;
  accountsPayable: any;
  monthlyIncome: number;
  monthlyExpense: number;
  pendingJournals: number;
  pendingReconciliation: number;
}

interface ReportState {
  data: ReportData | null;
  error: string | null;
  lastFetchTime: number | null;
  isLoading: boolean;
  yearDataCache: Record<string, { data: ReportData; fetchTime: number }>;
  fetchReportData: (forceRefresh?: boolean, year?: string) => Promise<void>;
  clearYearCache: (year?: string) => void;
  clearAllCache: () => void;
  incrementCacheValue: (year: string | undefined, field: keyof ReportData, increment: number) => void;
}


const fetchAllReports = async (year?: string): Promise<ReportData> => {

  const [
    accountData,
    journalData,
    invoiceData,
    trialData,
    incomeData,
    balanceData,
    receivablePayableData,
    monthlyData,
    pendingData
  ] = await Promise.all([
    getAccountSummary(year), // 🚨 修正：傳遞年度參數
    getJournalSummary(year), // 年度篩選：記帳分錄總數、分錄總金額
    getInvoiceSummary(year), // 年度篩選：發票總數、發票狀態
    getTrialBalance(year), // 年度篩選：用於其他報表頁面
    getIncomeStatement(year), // 年度篩選：用於其他報表頁面
    getBalanceSheet(year), // 年度篩選：用於其他報表頁面
    getAccountsReceivablePayable(year), // 🚨 修正：傳遞年度參數
    getMonthlyIncomeExpense(year), // 🚨 修正：傳遞年度參數
    getPendingTasks(year), // 部分年度篩選：待過帳分錄按年份
  ]);

  return {
    accountSummary: accountData.data,
    journalSummary: journalData.data,
    invoiceSummary: invoiceData.data,
    trialBalance: trialData.data,
    incomeStatement: incomeData.data,
    balanceSheet: balanceData.data,
    accountsReceivable: receivablePayableData.data.accountsReceivable,
    accountsPayable: receivablePayableData.data.accountsPayable,
    monthlyIncome: monthlyData.data.monthlyIncome,
    monthlyExpense: monthlyData.data.monthlyExpense,
    pendingJournals: pendingData.data.pendingJournals,
    pendingReconciliation: pendingData.data.pendingReconciliation,
  };
};

export const useReportStore = create<ReportState>()(
  persist(
    (set, get) => ({
      data: null,
      error: null,
      lastFetchTime: null,
      isLoading: false,
      yearDataCache: {},
      fetchReportData: async (forceRefresh = false, year?: string) => {
        const state = get();
        const cacheKey = year || 'current';

        // 檢查年份快取是否有效（非強制刷新時）
        if (!forceRefresh && state.yearDataCache[cacheKey]) {
          const cached = state.yearDataCache[cacheKey];
          // 快取永久有效，直到手動清除（與其他 store 一致）
          set({
            data: cached.data,
            lastFetchTime: cached.fetchTime,
            isLoading: false
          });
          return;
        }

        set({ error: null, isLoading: true });
        try {
          const reportData = await fetchAllReports(year);
          const fetchTime = Date.now();

          set({
            data: reportData,
            lastFetchTime: fetchTime,
            isLoading: false,
            yearDataCache: {
              ...state.yearDataCache,
              [cacheKey]: { data: reportData, fetchTime }
            }
          });
        } catch (err: any) {
          const errorMessage = err.message || 'An unknown error occurred';
          set({ error: errorMessage, isLoading: false });
        }
      },
      clearYearCache: (year?: string) => {
        const state = get();
        const cacheKey = year || 'current';
        const newCache = { ...state.yearDataCache };
        delete newCache[cacheKey];
        set({ yearDataCache: newCache });
      },
      clearAllCache: () => {
        set({
          yearDataCache: {},
          data: null,
          lastFetchTime: null
        });
      },
      incrementCacheValue: (year: string | undefined, field: keyof ReportData, increment: number) => {
        const state = get();
        const cacheKey = year || 'current';

        // 更新當前顯示的數據
        if (state.data && typeof state.data[field] === 'number') {
          set({
            data: {
              ...state.data,
              [field]: (state.data[field] as number) + increment
            }
          });
        }

        // 更新快取中的數據
        if (state.yearDataCache[cacheKey]) {
          const cached = state.yearDataCache[cacheKey];
          if (typeof cached.data[field] === 'number') {
            set({
              yearDataCache: {
                ...state.yearDataCache,
                [cacheKey]: {
                  ...cached,
                  data: {
                    ...cached.data,
                    [field]: (cached.data[field] as number) + increment
                  }
                }
              }
            });
          }
        }
      },
    }),
    {
      name: 'reports-storage',
      partialize: (state) => ({
        data: state.data,
        lastFetchTime: state.lastFetchTime,
        yearDataCache: state.yearDataCache,
      }),
    }
  )
);